'use client'

import { useState } from 'react'
import { PrimaryButton, SelectField, TextField } from '@/app/FormFields'

type User = {
  id: string
  email: string
  displayName: string | null
  role: 'line_manager' | 'expert' | 'admin'
  isActive: boolean
  authProvider: string
  lastLoginAt: string | null
  createdAt: string
}

const ROLE_LABELS: Record<string, string> = {
  line_manager: 'Line manager',
  expert: 'Expert',
  admin: 'Admin',
}

function formatDate(iso: string | null) {
  if (!iso) return 'Never'
  return new Intl.DateTimeFormat('en', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(iso))
}

export default function UsersClient({
  initialUsers,
  currentUserId,
}: {
  initialUsers: User[]
  currentUserId: string
}) {
  const [userList, setUserList] = useState<User[]>(initialUsers)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<User['role']>('line_manager')
  const [inviting, setInviting] = useState(false)
  const [inviteError, setInviteError] = useState<string | null>(null)
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault()
    if (!inviteEmail.trim()) return
    setInviting(true)
    setInviteError(null)
    setInviteSuccess(null)

    const res = await fetch('/api/admin/users', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: inviteEmail.trim(), role: inviteRole }),
    })

    const data = await res.json().catch(() => ({}))

    if (!res.ok) {
      setInviteError(data.error ?? 'Failed to invite user')
      setInviting(false)
      return
    }

    setInviteSuccess(`${inviteEmail.trim()} can now sign in. They have been sent an invite to their email.`)
    setInviteEmail('')
    // Refresh the list
    const listRes = await fetch('/api/admin/users')
    if (listRes.ok) {
      const updated = await listRes.json()
      setUserList(updated)
    }
    setInviting(false)
  }

  async function handlePatch(userId: string, patch: { role?: User['role']; isActive?: boolean }) {
    setActionError(null)
    const res = await fetch(`/api/admin/users/${userId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      setActionError(data.error ?? 'Action failed')
      return
    }
    setUserList((prev) =>
      prev.map((u) => (u.id === userId ? { ...u, ...patch } : u)),
    )
  }

  const active = userList.filter((u) => u.isActive)
  const disabled = userList.filter((u) => !u.isActive)

  return (
    <div className="space-y-8">
      {/* Invite form */}
      <section className="rounded-lg border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900">
        <h2 className="mb-4 text-sm font-semibold text-zinc-950 dark:text-white">Invite a new member</h2>
        <form onSubmit={handleInvite} className="flex flex-wrap gap-2">
          <TextField
            type="email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="name@example.com"
            required
            className="min-w-0 flex-1"
          />
          <SelectField
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value as User['role'])}
            className="w-auto min-w-40"
          >
            <option value="line_manager">Line manager</option>
            <option value="expert">Expert</option>
            <option value="admin">Admin</option>
          </SelectField>
          <PrimaryButton
            type="submit"
            disabled={inviting || !inviteEmail.trim()}
          >
            {inviting ? 'Adding…' : 'Add user'}
          </PrimaryButton>
        </form>
        {inviteError && <p className="mt-2 text-sm text-red-700 dark:text-red-300">{inviteError}</p>}
        {inviteSuccess && <p className="mt-2 text-sm text-emerald-700 dark:text-emerald-300">{inviteSuccess}</p>}
      </section>

      {actionError && (
        <p className="text-sm text-red-700 dark:text-red-300">{actionError}</p>
      )}

      {/* Active users table */}
      <section>
        <h2 className="mb-3 text-sm font-semibold text-zinc-950 dark:text-white">
          Active members <span className="font-normal text-zinc-500">({active.length})</span>
        </h2>
        <UserTable
          rows={active}
          currentUserId={currentUserId}
          onPatch={handlePatch}
          showDisable
        />
      </section>

      {/* Disabled users */}
      {disabled.length > 0 && (
        <section>
          <h2 className="mb-3 text-sm font-semibold text-zinc-500 dark:text-zinc-400">
            Disabled <span className="font-normal">({disabled.length})</span>
          </h2>
          <UserTable
            rows={disabled}
            currentUserId={currentUserId}
            onPatch={handlePatch}
            showDisable={false}
          />
        </section>
      )}
    </div>
  )
}

function UserTable({
  rows,
  currentUserId,
  onPatch,
  showDisable,
}: {
  rows: User[]
  currentUserId: string
  onPatch: (userId: string, patch: { role?: User['role']; isActive?: boolean }) => void
  showDisable: boolean
}) {
  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-zinc-200 bg-white px-6 py-10 text-center text-sm text-zinc-500 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-400">
        None
      </div>
    )
  }

  return (
    <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-zinc-200 bg-zinc-50 text-left dark:border-zinc-800 dark:bg-zinc-800/50">
            <th className="px-4 py-3 font-medium text-zinc-700 dark:text-zinc-300">Member</th>
            <th className="px-4 py-3 font-medium text-zinc-700 dark:text-zinc-300">Role</th>
            <th className="hidden px-4 py-3 font-medium text-zinc-700 sm:table-cell dark:text-zinc-300">Last login</th>
            <th className="px-4 py-3" />
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800">
          {rows.map((user) => {
            const isSelf = user.id === currentUserId
            return (
              <tr key={user.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/50">
                <td className="px-4 py-3">
                  <p className="font-medium text-zinc-950 dark:text-zinc-50">
                    {user.displayName ?? user.email}
                    {isSelf && (
                      <span className="ml-2 rounded-full bg-zinc-100 px-2 py-0.5 text-xs text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400">
                        you
                      </span>
                    )}
                  </p>
                  {user.displayName && (
                    <p className="text-xs text-zinc-500 dark:text-zinc-400">{user.email}</p>
                  )}
                </td>
                <td className="px-4 py-3">
                  {isSelf ? (
                    <span className="text-zinc-700 dark:text-zinc-300">{ROLE_LABELS[user.role]}</span>
                  ) : (
                    <SelectField
                      value={user.role}
                      onChange={(e) => onPatch(user.id, { role: e.target.value as User['role'] })}
                      className="h-9 w-auto px-3 text-sm"
                    >
                      <option value="line_manager">Line manager</option>
                      <option value="expert">Expert</option>
                      <option value="admin">Admin</option>
                    </SelectField>
                  )}
                </td>
                <td className="hidden px-4 py-3 text-zinc-500 sm:table-cell dark:text-zinc-400">
                  {formatDate(user.lastLoginAt)}
                </td>
                <td className="px-4 py-3 text-right">
                  {!isSelf && showDisable && (
                    <button
                      onClick={() => onPatch(user.id, { isActive: false })}
                      className="text-xs text-red-600 hover:underline dark:text-red-400"
                    >
                      Disable
                    </button>
                  )}
                  {!isSelf && !showDisable && (
                    <button
                      onClick={() => onPatch(user.id, { isActive: true })}
                      className="text-xs text-sky-700 hover:underline dark:text-sky-400"
                    >
                      Re-enable
                    </button>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
